import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  CheckCircle,
  Shield,
  Award,
  Users,
  BookOpen,
  TrendingUp,
  FileText,
  ArrowRight,
  MapPin,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Mail
} from 'lucide-react';

interface FAQ {
  id: string;
  categoria: string;
  domanda: string;
  risposta: string;
}

/**
 * Pagina informativa sulla Rete Certificata UNASCA
 */
const CertifiedNetworkPage: React.FC = () => {
  const [expandedFAQs, setExpandedFAQs] = useState<string[]>([]);

  const toggleFAQ = (id: string) => {
    setExpandedFAQs(prev =>
      prev.includes(id) ? prev.filter(faqId => faqId !== id) : [...prev, id]
    );
  };

  const faqs: FAQ[] = [
    // Adesione alla Rete Certificata
    {
      id: 'faq-1',
      categoria: 'Adesione alla Rete Certificata',
      domanda: 'Come si aderisce alla Rete Certificata?',
      risposta: 'È possibile aderire compilando il form disponibile sul sito Unasca Studi alla sezione dedicata alla Rete Certificata.'
    },
    {
      id: 'faq-2',
      categoria: 'Adesione alla Rete Certificata',
      domanda: 'Quali documenti sono necessari per l\'iscrizione?',
      risposta: 'È sufficiente compilare il modulo di adesione. Non è richiesta documentazione aggiuntiva, salvo diversa richiesta successiva.'
    },
    {
      id: 'faq-3',
      categoria: 'Adesione alla Rete Certificata',
      domanda: 'La mia agenzia non compare sulla mappa degli studi aderenti. Cosa devo fare?',
      risposta: 'Se l\'adesione è già stata inviata, la presenza in mappa viene aggiornata progressivamente. In caso di ritardi è possibile segnalarlo: la situazione verrà verificata e aggiornata.'
    },
    {
      id: 'faq-4',
      categoria: 'Adesione alla Rete Certificata',
      domanda: 'Posso aderire anche se sono un consorziato di un\'altra piattaforma o rete?',
      risposta: 'Sì, l\'adesione è possibile anche se si è consorziati con altre realtà, purché siano rispettati i requisiti previsti dal regolamento.'
    },
    {
      id: 'faq-5',
      categoria: 'Adesione alla Rete Certificata',
      domanda: 'Posso aderire anche se non ho ancora ricevuto il GDPR dal mio consorzio?',
      risposta: 'Sì, è comunque possibile presentare la richiesta. Il GDPR dovrà essere regolarizzato successivamente.'
    },
    // Requisiti contrattuali e contributivi
    {
      id: 'faq-6',
      categoria: 'Requisiti contrattuali e contributivi',
      domanda: 'È obbligatorio applicare il CCNL Autoscuole e Agenzie di Pratiche Auto per aderire alla rete?',
      risposta: 'No. Non è obbligatorio applicare questo contratto collettivo per aderire alla Rete Certificata.'
    },
    {
      id: 'faq-7',
      categoria: 'Requisiti contrattuali e contributivi',
      domanda: 'È obbligatorio il versamento del contributo EBAS?',
      risposta: 'Il versamento a EBAS è richiesto per la partecipazione ai servizi formativi e alle iniziative della rete. Non è però obbligatorio applicare un contratto specifico.'
    },
    {
      id: 'faq-8',
      categoria: 'Requisiti contrattuali e contributivi',
      domanda: 'Cosa succede se la mia azienda applica un CCNL diverso da quello delle Autoscuole (ad esempio Commercio)?',
      risposta: 'L\'adesione alla rete è comunque possibile. È possibile versare il contributo EBAS anche applicando un contratto differente.'
    },
    {
      id: 'faq-9',
      categoria: 'Requisiti contrattuali e contributivi',
      domanda: 'Abbiamo scelto l\'elemento aggiuntivo in busta paga in alternativa a EBAS: è valido per l\'adesione?',
      risposta: 'No. È importante dichiarare se si aderisce o meno a EBAS: l\'adesione consente l\'accesso ai corsi gratuiti dedicati ai dipendenti.'
    },
    // Funzionamento della rete
    {
      id: 'faq-10',
      categoria: 'Funzionamento della rete',
      domanda: 'Come funziona lo scambio e l\'acquisto coordinato dei crediti PagoPA?',
      risposta: 'La rete facilita il coordinamento tra studi per ottimizzare gli acquisti, ridurre le giacenze e favorire lo scambio dei crediti secondo le regole PagoPA.'
    },
    {
      id: 'faq-11',
      categoria: 'Funzionamento della rete',
      domanda: 'Come viene gestito il supporto per i rimborsi PagoPA?',
      risposta: 'La rete permette di recuperare e redistribuire i crediti rimborsati per evitare sprechi e ottimizzare il lavoro degli studi aderenti.'
    },
    {
      id: 'faq-12',
      categoria: 'Funzionamento della rete',
      domanda: 'Qual è la quota annuale di certificazione?',
      risposta: 'La quota è stabilita dal Comitato di Gestione. Per le annualità 2025/2026 è prevista una singola quota di 50,00 €.'
    },
    // Formazione e Corsi
    {
      id: 'faq-13',
      categoria: 'Formazione e Corsi',
      domanda: 'I corsi saranno online o in presenza?',
      risposta: 'I corsi previsti si terranno sia in presenza sia online. Le sedi degli incontri in presenza verranno comunicate successivamente.'
    },
    {
      id: 'faq-14',
      categoria: 'Formazione e Corsi',
      domanda: 'Quando inizieranno i corsi?',
      risposta: 'I corsi saranno attivi dal 1° gennaio 2027 al 31 dicembre 2028.'
    },
    {
      id: 'faq-15',
      categoria: 'Formazione e Corsi',
      domanda: 'Possono partecipare anche soci e dipendenti?',
      risposta: 'Sì. I corsi sono aperti a titolari, soci e dipendenti dell\'agenzia.'
    },
    {
      id: 'faq-16',
      categoria: 'Formazione e Corsi',
      domanda: 'Se ho già l\'abilitazione professionale, devo comunque frequentare i corsi iniziali?',
      risposta: 'L\'esonero potrà essere valutato, ma la frequenza ai corsi di aggiornamento rimane prevista.'
    },
    {
      id: 'faq-17',
      categoria: 'Formazione e Corsi',
      domanda: 'I corsi di aggiornamento saranno disponibili online?',
      risposta: 'Sì, è prevista anche la modalità online.'
    },
    // Autentiche e responsabilità
    {
      id: 'faq-18',
      categoria: 'Autentiche e responsabilità',
      domanda: 'Quando un collega chiede di autenticare un atto di vendita, come si gestisce la privacy dell\'acquirente?',
      risposta: 'Chi autentica deve raccogliere il consenso privacy firmato dall\'acquirente e ricevere la documentazione completa dallo studio richiedente. La rete fornisce modelli e procedure condivise.'
    },
    {
      id: 'faq-19',
      categoria: 'Autentiche e responsabilità',
      domanda: 'Chi è responsabile se l\'atto autenticato non viene trascritto dal collega che lo ha richiesto?',
      risposta: 'La responsabilità è dello studio che prende in carico la trascrizione. Chi autentica risponde solo della correttezza dell\'autentica. La Rete Certificata vigila sugli studi e interviene in caso di inadempienze.'
    },
    // Assicurazione professionale
    {
      id: 'faq-20',
      categoria: 'Assicurazione professionale',
      domanda: 'È disponibile una polizza assicurativa professionale?',
      risposta: 'Sì, la rete prevede una polizza dedicata. Gli estremi per il pagamento e le modalità di attivazione vengono comunicati al momento della disponibilità del gestionale.'
    },
    // Ulteriori informazioni
    {
      id: 'faq-21',
      categoria: 'Ulteriori informazioni',
      domanda: 'Dopo l\'adesione, cosa succede?',
      risposta: 'Una volta registrata l\'adesione, gli studi vengono ricontattati per valutare insieme il percorso certificativo e l\'inserimento nella rete.'
    },
    {
      id: 'faq-22',
      categoria: 'Ulteriori informazioni',
      domanda: 'Chi non aderisce alla rete subirà cambiamenti nelle proprie modalità operative?',
      risposta: 'No. Chi non aderisce continua a svolgere le proprie attività senza modifiche.'
    },
    {
      id: 'faq-23',
      categoria: 'Ulteriori informazioni',
      domanda: 'La rete prevede rapporti diretti con l\'utente privato?',
      risposta: 'La rete migliora l\'organizzazione e la qualità del servizio, senza imporre un modello di distacco dagli attuali fornitori o concessionari.'
    }
  ];

  // Raggruppa FAQ per categoria
  const categorieUniche = Array.from(new Set(faqs.map(faq => faq.categoria)));

  const vantaggi = [
    {
      Icona: Shield,
      titolo: 'Garanzia UNASCA',
      testo: 'La Rete Certificata nasce da UNASCA, la maggiore associazione di categoria del settore, per garantire standard elevati di professionalità e qualità negli studi aderenti.'
    },
    {
      Icona: TrendingUp,
      titolo: 'Visibilità Nazionale',
      testo: 'Presenza sul portale www.unasca.it e campagne di comunicazione dedicate su social media e canali digitali per aumentare la tua visibilità.'
    },
    {
      Icona: Award,
      titolo: 'Certificazione Ufficiale',
      testo: 'Certificato di garanzia in formato cartaceo e digitale, realizzato dal Poligrafico e Zecca dello Stato con elementi anticontraffazione.'
    },
    {
      Icona: BookOpen,
      titolo: 'Formazione Continua',
      testo: 'Accesso a corsi specialistici presso il Centro Studi "Cesare Ferrari" per mantenere elevati standard professionali.'
    },
    {
      Icona: Users,
      titolo: 'Rete di Collaborazione',
      testo: 'Collaborazione obbligatoria con altri membri per svincolo atti, scambio crediti PagoPA e gestione rimborsi.'
    },
    {
      Icona: CheckCircle,
      titolo: 'Standard di Qualità',
      testo: 'Conformità a rigorosi standard di qualità in materia di GDPR, cybersecurity, assicurazione professionale e formazione.'
    }
  ];

  const requisiti = [
    { titolo: 'Iscrizione UNASCA', descrizione: "Essere membri attivi di UNASCA in regola con la quota associativa per l'anno in corso." },
    { titolo: 'Conformità Normativa', descrizione: 'Rispetto delle normative vigenti, codice etico UNASCA, licenze e autorizzazioni richieste.' },
    { titolo: 'GDPR e Privacy', descrizione: 'Conformità al GDPR con nomina di un Responsabile della Protezione dei Dati (DPO).' },
    { titolo: 'Cybersecurity', descrizione: 'Adozione di misure di sicurezza informatica (antivirus, firewall, backup).' },
    { titolo: 'Assicurazione Professionale', descrizione: 'Possesso di assicurazione professionale (esenti i consorziati Sermetra).' },
    { titolo: 'EBAS', descrizione: 'Versamento dei diritti stabiliti al fondo EBAS per i dipendenti.' },
    { titolo: 'Esperienza', descrizione: 'Attività da almeno un anno con lista dettagliata dei servizi offerti.' },
    { titolo: 'Formazione Continua', descrizione: "Impegno alla partecipazione ai corsi del Centro Studi 'Cesare Ferrari'." }
  ];

  return (
    <>
      <Helmet>
        <title>Rete Certificata UNASCA — Informazioni per gli studi</title>
        <meta name="description" content="Vantaggi, requisiti, regolamento e domande frequenti della Rete Certificata UNASCA: la certificazione degli studi di consulenza automobilistica con attestato del Poligrafico e Zecca dello Stato." />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">

        {/* ═══ HERO ═══ */}
        <section className="relative overflow-hidden bg-primary-950 text-white">
          <div className="absolute inset-y-0 right-0 w-full lg:w-[62%]">
            <img
              src="/images/rete-certificata-studi/studio-consegna.webp"
              srcSet="/images/rete-certificata-studi/studio-consegna-1280.webp 1280w, /images/rete-certificata-studi/studio-consegna.webp 2400w"
              sizes="(min-width: 1024px) 62vw, 100vw"
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              className="h-full w-full object-cover object-[70%_center]"
            />
          </div>
          <div className="absolute inset-0 bg-primary-950/85 lg:bg-transparent lg:bg-gradient-to-r lg:from-primary-950 lg:from-[38%] lg:via-primary-950/75 lg:via-[52%] lg:to-transparent" />

          <div className="relative container-custom pt-6 pb-16 md:pb-24">
            {/* Breadcrumb */}
            <nav aria-label="Percorso" className="mb-10 md:mb-14">
              <ol className="flex items-center gap-2 text-sm text-white/60">
                <li>
                  <Link to="/rete-certificata" className="inline-flex items-center gap-1 hover:text-white transition-colors">
                    <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                    Trova uno studio
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="font-medium text-white/90">Informazioni per gli studi</li>
              </ol>
            </nav>

            <div className="max-w-xl motion-safe:animate-fade-in">
              <img
                src="/images/rete-certificata-studi/logo-rete-certificata-bianco.webp"
                alt="Logo Rete Certificata UNASCA"
                width={112}
                height={112}
                className="mb-6 h-24 w-24 md:h-28 md:w-28 drop-shadow-[0_4px_16px_rgba(0,0,0,0.35)]"
              />
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight tracking-tight [text-wrap:balance]">
                Rete Certificata UNASCA
              </h1>
              <p className="text-lg md:text-xl font-semibold text-primary-200 mb-4">
                Un contrassegno di Stato a tutela del cittadino
              </p>
              <p className="text-base md:text-lg text-white/80 mb-8">
                La Rete Certificata UNASCA rappresenta l'eccellenza nel settore della consulenza automobilistica,
                garantendo ai cittadini servizi di qualità certificata e professionalità riconosciuta.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/rete-certificata/iscrizione"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-base font-semibold text-primary-800 shadow-lg hover:bg-primary-50 transition-colors group"
                >
                  Iscriviti Ora
                  <ArrowRight className="w-5 h-5 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
                <Link
                  to="/rete-certificata"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-6 py-3.5 text-base font-semibold text-white ring-1 ring-white/30 backdrop-blur-sm hover:bg-white/20 transition-colors"
                >
                  <MapPin className="w-5 h-5" aria-hidden="true" />
                  Visualizza Mappa
                </Link>
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute bottom-16 right-10 hidden -rotate-6 text-right text-white lg:block xl:right-16" aria-hidden="true">
            <span className="block text-4xl leading-tight drop-shadow-lg" style={{ fontFamily: "'Caveat', cursive" }}>
              Professionalità<br />riconosciuta
            </span>
            <svg viewBox="0 0 220 16" className="ml-auto mt-1 w-52 text-primary-400" fill="none">
              <path d="M3 12 C 60 2, 140 2, 217 8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>
        </section>

        {/* ═══ IMMAGINE RETE CERTIFICATA ═══ */}
        <section className="py-14 md:py-16 bg-white dark:bg-gray-900">
          <div className="container-custom">
            <div className="grid lg:grid-cols-12 gap-10 items-center max-w-6xl mx-auto">
              <div className="lg:col-span-5 text-center lg:text-left">
                <img
                  src="/images/rete-certificata-studi/logo-rete-certificata-verde.webp"
                  alt=""
                  aria-hidden="true"
                  width={160}
                  height={160}
                  className="mx-auto lg:mx-0 mb-6 h-32 w-32 md:h-40 md:w-40 dark:brightness-110"
                />
                <Occhiello>Il marchio della Rete</Occhiello>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
                  Un segno di riconoscimento per il cittadino
                </h2>
                <p className="text-base text-gray-600 dark:text-gray-400">
                  Gli studi aderenti espongono il marchio della Rete Certificata: chi lo vede sa di trovarsi
                  davanti a professionisti verificati da UNASCA.
                </p>
              </div>
              <div className="lg:col-span-7">
                <img
                  src="/images/rete-certificata-studi/infografica-rete.webp"
                  srcSet="/images/rete-certificata-studi/infografica-rete-1000.webp 1000w, /images/rete-certificata-studi/infografica-rete.webp 2000w"
                  sizes="(min-width: 1024px) 40rem, 100vw"
                  width={2000}
                  height={1131}
                  alt="Rete Certificata UNASCA: mappa d'Italia con la rete degli studi e i requisiti GDPR e privacy, cybersecurity, assicurazione professionale e formazione continua"
                  loading="lazy"
                  className="w-full h-auto rounded-2xl shadow-xl ring-1 ring-black/5 dark:ring-white/10"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ═══ VANTAGGI ═══ */}
        <section className="py-16 md:py-20 bg-primary-50/70 dark:bg-gray-950">
          <div className="container-custom">
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <Occhiello>Perché aderire</Occhiello>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                Vantaggi della Rete Certificata
              </h2>
              <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                Entrare a far parte della Rete Certificata significa accedere a un network di eccellenza
                con vantaggi esclusivi per il tuo studio.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {vantaggi.map(({ Icona, titolo, testo }) => (
                <div
                  key={titolo}
                  className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-sm ring-1 ring-black/5 dark:ring-white/10 hover:shadow-lg transition-shadow"
                >
                  <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-primary-50 dark:bg-primary-900/20" aria-hidden="true" />
                  <div className="relative w-12 h-12 bg-primary-700 rounded-full flex items-center justify-center mb-4 shadow">
                    <Icona className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                  <h3 className="relative text-lg font-semibold text-gray-900 dark:text-white mb-2">{titolo}</h3>
                  <p className="relative text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed">{testo}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ POLIGRAFICO DELLO STATO ═══ */}
        <section className="py-16 md:py-20 bg-white dark:bg-gray-900">
          <div className="container-custom">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12 max-w-3xl mx-auto">
                <Occhiello>Certificazione</Occhiello>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-5 tracking-tight">
                  Attestato Ufficiale del Poligrafico dello Stato
                </h2>
                <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 mb-3">
                  L'adesione alla <strong>Rete Certificata UNASCA</strong> è certificata da un attestato ufficiale
                  rilasciato dal <strong>Poligrafico e Zecca dello Stato Italiano</strong>.
                </p>
                <p className="text-base text-gray-600 dark:text-gray-400">
                  Un documento di prestigio con elementi di sicurezza anticontraffazione,
                  che garantisce l'autenticità e l'ufficialità della certificazione.
                </p>
              </div>

              <div className="grid md:grid-cols-5 gap-6 items-stretch">
                {/* Logo IPZS */}
                <div className="md:col-span-2 flex flex-col rounded-2xl bg-gray-50 dark:bg-gray-950 p-8 ring-1 ring-black/5 dark:ring-white/10">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      Istituto Poligrafico e Zecca dello Stato
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Ente certificatore ufficiale
                    </p>
                  </div>
                  <div className="flex flex-1 items-center justify-center rounded-xl bg-white p-8 shadow-sm">
                    <img
                      src="/images/IPZS_logo.svg"
                      alt="Logo Poligrafico e Zecca dello Stato"
                      loading="lazy"
                      className="max-w-full h-auto max-h-32"
                    />
                  </div>
                </div>

                {/* Immagine Attestato */}
                <figure className="md:col-span-3 rounded-2xl bg-gray-50 dark:bg-gray-950 p-4 ring-1 ring-black/5 dark:ring-white/10">
                  <img
                    src="/images/poligrafico.jpg"
                    alt="Attestato Rete Certificata UNASCA"
                    loading="lazy"
                    className="w-full h-80 md:h-[26rem] object-cover object-[center_40%] rounded-xl shadow-lg"
                  />
                  <figcaption className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4 font-semibold">
                    Esempio di Attestato Ufficiale
                  </figcaption>
                </figure>
              </div>

              {/* Box Informativo */}
              <div className="relative mt-8 overflow-hidden rounded-2xl bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800 p-8 md:p-10 text-white shadow-xl">
                <div
                  className="absolute inset-0 opacity-10 text-primary-300"
                  style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }}
                  aria-hidden="true"
                />
                <div className="relative flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                  <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
                    <Shield className="w-7 h-7 text-primary-200" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Garanzia di Autenticità</h3>
                    <p className="text-base text-white/80 max-w-3xl">
                      Ogni attestato è dotato di <strong className="text-white">elementi di sicurezza anticontraffazione</strong>
                      {' '}e viene rilasciato sia in <strong className="text-white">formato cartaceo</strong> che <strong className="text-white">digitale</strong>,
                      garantendo la massima tutela e riconoscibilità della certificazione UNASCA.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ REQUISITI ═══ */}
        <section className="py-16 md:py-20 bg-gray-50 dark:bg-gray-950">
          <div className="container-custom">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12 max-w-3xl mx-auto">
                <Occhiello>Come si entra</Occhiello>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                  Requisiti di Accesso
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                  Per entrare nella Rete Certificata è necessario soddisfare i seguenti requisiti:
                </p>
              </div>

              <ol className="grid sm:grid-cols-2 gap-4">
                {requisiti.map(({ titolo, descrizione }, i) => (
                  <RequisitoItem key={titolo} numero={String(i + 1)} titolo={titolo} descrizione={descrizione} />
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* ═══ REGOLAMENTO E VIDEO ═══ */}
        <section className="py-16 md:py-20 bg-white dark:bg-gray-900">
          <div className="container-custom">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="text-center lg:text-left">
                <div className="mx-auto lg:mx-0 mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary-700 shadow">
                  <FileText className="w-7 h-7 text-white" aria-hidden="true" />
                </div>
                <Occhiello>Le regole della Rete</Occhiello>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                  Regolamento Completo
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 mb-8">
                  Consulta il regolamento completo per conoscere tutti i dettagli su requisiti,
                  materie di certificazione, governance e vantaggi della Rete Certificata.
                </p>
                <Link
                  to="/rete-certificata/regolamento"
                  className="btn bg-primary-800 text-white hover:bg-primary-900 focus:ring-primary-500 group"
                >
                  <FileText className="w-5 h-5 mr-2" aria-hidden="true" />
                  Leggi il Regolamento
                  <ArrowRight className="w-5 h-5 ml-2 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              </div>

              {/* Video Presentazione */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-1">
                  Video di Presentazione della Rete Certificata
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-5">(attiva audio se preferisci)</p>
                <div className="relative max-w-sm mx-auto">
                  <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-primary-200 to-primary-50 dark:from-primary-900/50 dark:to-transparent" aria-hidden="true" />
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
                    <video
                      controls
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-auto"
                    >
                      <source src="/images/giuseppe_retecert.mp4" type="video/mp4" />
                      Il tuo browser non supporta il tag video.
                    </video>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ FAQ ═══ */}
        <section className="py-16 md:py-20 bg-primary-50/70 dark:bg-gray-950">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-700 rounded-full mb-6 shadow">
                  <HelpCircle className="w-7 h-7 text-white" aria-hidden="true" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                  Domande Frequenti
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                  Trova le risposte alle domande più comuni sulla Rete Certificata UNASCA
                </p>
              </div>

              <div className="space-y-10">
                {categorieUniche.map((categoria) => {
                  const faqsCategoria = faqs.filter(faq => faq.categoria === categoria);

                  return (
                    <div key={categoria}>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <span className="w-1 h-5 bg-primary-600 dark:bg-primary-500 rounded-full" aria-hidden="true" />
                        {categoria}
                      </h3>

                      <div className="space-y-3">
                        {faqsCategoria.map((faq) => {
                          const aperta = expandedFAQs.includes(faq.id);
                          return (
                            <div
                              key={faq.id}
                              className={`overflow-hidden rounded-xl bg-white dark:bg-gray-900 ring-1 transition-shadow ${
                                aperta
                                  ? 'ring-primary-300 dark:ring-primary-700 shadow-md'
                                  : 'ring-black/5 dark:ring-white/10 hover:ring-primary-200 dark:hover:ring-primary-800'
                              }`}
                            >
                              <button
                                onClick={() => toggleFAQ(faq.id)}
                                aria-expanded={aperta}
                                className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                              >
                                <span className="text-sm md:text-base font-semibold text-gray-900 dark:text-white">
                                  {faq.domanda}
                                </span>
                                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${aperta ? 'bg-primary-700 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                                  {aperta ? (
                                    <ChevronUp className="w-4 h-4" aria-hidden="true" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" aria-hidden="true" />
                                  )}
                                </span>
                              </button>

                              {aperta && (
                                <div className="px-5 pb-5 pt-1">
                                  <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {faq.risposta}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Contatto per altre domande */}
              <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-2xl bg-white dark:bg-gray-900 p-6 md:p-8 shadow-sm ring-1 ring-black/5 dark:ring-white/10 text-center sm:text-left">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    Non hai trovato la risposta che cercavi?
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                    Contattaci per qualsiasi altra domanda sulla Rete Certificata
                  </p>
                </div>
                <a
                  href="mailto:retecertificata@unasca.it"
                  className="inline-flex flex-shrink-0 items-center gap-2 rounded-xl bg-primary-700 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-800 transition-colors"
                >
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  retecertificata@unasca.it
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ CTA ═══ */}
        <section className="relative overflow-hidden bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800 text-white">
          <div
            className="absolute inset-0 opacity-10 text-primary-300"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }}
            aria-hidden="true"
          />
          <img
            src="/images/rete-certificata-studi/logo-rete-certificata-bianco.webp"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="pointer-events-none absolute -right-16 -bottom-16 hidden h-80 w-80 opacity-10 md:block"
          />
          <div className="relative container-custom py-16 md:py-20 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
              Pronto a Entrare nella Rete Certificata?
            </h2>
            <p className="text-base md:text-lg mb-8 text-white/80 max-w-2xl mx-auto">
              Compila la domanda di adesione online e inizia il percorso per ottenere
              la certificazione UNASCA.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/rete-certificata/iscrizione"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-base font-semibold text-primary-800 shadow-lg hover:bg-primary-50 transition-colors group"
              >
                Inizia la Domanda
                <ArrowRight className="w-5 h-5 motion-safe:group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
              <Link
                to="/rete-certificata"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-6 py-3.5 text-base font-semibold text-white ring-1 ring-white/30 hover:bg-white/20 transition-colors"
              >
                <MapPin className="w-5 h-5" aria-hidden="true" />
                Visualizza Mappa Studi
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

// Occhiello sopra i titoli di sezione
const Occhiello: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-400">{children}</p>
);

// Componente helper per i requisiti
interface RequisitoItemProps {
  numero: string;
  titolo: string;
  descrizione: string;
}

const RequisitoItem: React.FC<RequisitoItemProps> = ({ numero, titolo, descrizione }) => (
  <li className="flex gap-4 rounded-2xl bg-white dark:bg-gray-900 p-5 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
    <div className="flex-shrink-0">
      <div className="w-10 h-10 bg-primary-700 text-white rounded-full flex items-center justify-center font-bold font-sans shadow">
        {numero}
      </div>
    </div>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{titolo}</h3>
      <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">{descrizione}</p>
    </div>
  </li>
);

export default CertifiedNetworkPage;
